import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { mockLocations } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SectionHeader from '../components/SectionHeader';
import IconBadge from '../components/IconBadge';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import EditableText from '../components/EditableText';
import { submitContact } from '../lib/api';
import { BACKEND_URL, BTN_PRIMARY, DEFAULT_BRAND } from '../config/brand';
import { CENTER_IMAGES } from '../config/images';
import { useSettings } from '../context/SettingsContext';

const mapApiCenter = (c) => ({
  id: c.id,
  name: c.name,
  address: c.address ? `${c.address}, ${c.city}` : c.city,
  phone: c.phone,
  email: c.email,
  image: CENTER_IMAGES[c.slug] || CENTER_IMAGES['nice-gambetta'],
});

const SUBJECT_LABELS = {
  'devis-entreprise': 'Devis entreprise / B2B',
  boutique: 'Conseil achat robot — boutique',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass = 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-[#3a00e0]';
const inputErrorClass = 'border-red-500/70 focus:border-red-500';

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="text-red-600 text-xs mt-1 flex items-center gap-1" role="alert" aria-live="polite">
      <AlertCircle className="h-3 w-3 shrink-0" aria-hidden />
      {message}
    </p>
  );
}

const Contact = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { settings } = useSettings();
  const contactPhone = settings.contact_phone || DEFAULT_BRAND.contact_phone;
  const contactEmail = settings.contact_email || DEFAULT_BRAND.contact_email;
  const contactAddress = settings.contact_address || DEFAULT_BRAND.contact_address;
  const phoneHref = `tel:${String(contactPhone).replace(/\s/g, '')}`;
  const [loading, setLoading] = useState(false);
  const [centersLoading, setCentersLoading] = useState(false);
  const [locations, setLocations] = useState(mockLocations);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const errors = useMemo(() => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Le nom est requis';
    if (!formData.email.trim()) e.email = 'L\'email est requis';
    else if (!EMAIL_RE.test(formData.email.trim())) e.email = 'Format d\'email invalide';
    if (!formData.subject.trim()) e.subject = 'Le sujet est requis';
    if (!formData.message.trim()) e.message = 'Le message est requis';
    else if (formData.message.trim().length < 10) e.message = 'Le message doit contenir au moins 10 caractères';
    return e;
  }, [formData]);

  const isValid = Object.keys(errors).length === 0;

  useEffect(() => {
    const subjectKey = searchParams.get('subject');
    const robotName = location.state?.robotName;
    setFormData((prev) => {
      const next = { ...prev };
      if (subjectKey && SUBJECT_LABELS[subjectKey]) {
        next.subject = SUBJECT_LABELS[subjectKey];
      }
      if (robotName && !prev.message) {
        next.message = `Bonjour,\n\nJe suis intéressé(e) par le robot « ${robotName} ».\n\n`;
      }
      return next;
    });
  }, [searchParams, location.state]);

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadCenters = async () => {
      try {
        setCentersLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/centers`);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setLocations(data.map(mapApiCenter));
        }
      } catch {
        // fallback mock
      } finally {
        setCentersLoading(false);
      }
    };
    loadCenters();
  }, []);

  const handleChange = (field) => (e) => {
    setSubmitStatus(null);
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, subject: true, message: true });
    if (!isValid) return;

    try {
      setLoading(true);
      setSubmitStatus(null);
      await submitContact(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTouched({});
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = (field) =>
    `${inputClass} ${touched[field] && errors[field] ? inputErrorClass : ''}`;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        eyebrow="Contact"
        eyebrowKey="contact.hero.eyebrow"
        title="Une question ? Parlons-en"
        titleKey="contact.hero.title"
        subtitle="Besoin de renseignements ? Notre équipe est là pour vous répondre."
        subtitleKey="contact.hero.subtitle"
      />

      <CircuitDivider className="mb-0" />

      <section className="py-14 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12">
            <Reveal animation="fade-up" className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm">
              <div className="mb-6">
                <h2 className="text-slate-900 text-xl sm:text-2xl font-semibold mb-2">
                  <EditableText
                    editKey="contact.form.title"
                    defaultValue="Envoyez-nous un message"
                    as="span"
                    className="text-slate-900 text-xl sm:text-2xl font-semibold"
                  />
                </h2>
                <p className="text-slate-600 text-sm sm:text-base">
                  <EditableText
                    editKey="contact.form.subtitle"
                    defaultValue="Nous vous répondrons dans les plus brefs délais (sous 24 h ouvrées)"
                    as="span"
                    multiline
                    className="text-slate-600"
                  />
                </p>
              </div>
              <div>
                {submitStatus === 'success' && (
                  <Alert className="mb-6 bg-green-500/10 border-green-500/30 text-green-600 animate-in fade-in duration-300" aria-live="polite">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <AlertTitle className="text-green-600">Message envoyé !</AlertTitle>
                    <AlertDescription className="text-green-600/80">
                      Merci pour votre message. Notre équipe vous répondra très prochainement.
                    </AlertDescription>
                  </Alert>
                )}
                {submitStatus === 'error' && (
                  <Alert variant="destructive" className="mb-6 bg-red-500/10 border-red-500/30 animate-in fade-in duration-300" aria-live="assertive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Envoi impossible</AlertTitle>
                    <AlertDescription>
                      Une erreur est survenue. Vérifiez votre connexion ou réessayez dans quelques instants.
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-600">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        onBlur={handleBlur('name')}
                        className={fieldClass('name')}
                        placeholder="Jean Dupont"
                        aria-invalid={touched.name && !!errors.name}
                        aria-describedby={touched.name && errors.name ? 'name-error' : undefined}
                      />
                      {touched.name && <FieldError id="name-error" message={errors.name} />}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-600">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange('email')}
                        onBlur={handleBlur('email')}
                        className={fieldClass('email')}
                        placeholder="jean.dupont@example.com"
                        aria-invalid={touched.email && !!errors.email}
                        aria-describedby={touched.email && errors.email ? 'email-error' : undefined}
                      />
                      {touched.email && <FieldError id="email-error" message={errors.email} />}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-600">Téléphone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange('phone')}
                        onBlur={handleBlur('phone')}
                        className={inputClass}
                        placeholder="06 XX XX XX XX"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-slate-600">Sujet *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={handleChange('subject')}
                        onBlur={handleBlur('subject')}
                        className={fieldClass('subject')}
                        placeholder="Renseignements atelier découverte"
                        aria-invalid={touched.subject && !!errors.subject}
                        aria-describedby={touched.subject && errors.subject ? 'subject-error' : undefined}
                      />
                      {touched.subject && <FieldError id="subject-error" message={errors.subject} />}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-600">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={handleChange('message')}
                      onBlur={handleBlur('message')}
                      rows={5}
                      className={`${fieldClass('message')} resize-none`}
                      placeholder="Décrivez votre demande…"
                      aria-invalid={touched.message && !!errors.message}
                      aria-describedby={touched.message && errors.message ? 'message-error' : undefined}
                    />
                    {touched.message && <FieldError id="message-error" message={errors.message} />}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-full ${BTN_PRIMARY}`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Envoi en cours…
                      </>
                    ) : (
                      <>
                        Envoyer le message
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
            </Reveal>

            <div className="lg:col-span-2 space-y-8">
              <Reveal animation="fade-up" delay={80}>
              <div>
                <h2 className="text-slate-900 font-semibold text-lg mb-1">
                  <EditableText
                    editKey="contact.info.title"
                    defaultValue="Informations de contact"
                    as="span"
                    className="text-slate-900 font-semibold"
                  />
                </h2>
                <p className="text-slate-600 text-sm mb-5">
                  <EditableText
                    editKey="contact.info.subtitle"
                    defaultValue="Horaires et coordonnées générales"
                    as="span"
                    className="text-slate-600"
                  />
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <IconBadge icon={Phone} variant="purple" size="sm" />
                    <div>
                      <EditableText
                        editKey="contact.info.phone_label"
                        defaultValue="Téléphone"
                        as="h3"
                        className="text-slate-900 font-semibold mb-1"
                      />
                      <a href={phoneHref} className="text-[#3a00e0] text-sm transition-colors hover:underline underline-offset-2">
                        {contactPhone}
                      </a>
                      <EditableText
                        editKey="contact.info.phone_hours"
                        defaultValue="Lun–Sam : 9h–18h"
                        as="p"
                        className="text-slate-500 text-xs mt-0.5"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IconBadge icon={Mail} variant="orange" size="sm" />
                    <div>
                      <EditableText
                        editKey="contact.info.email_label"
                        defaultValue="Email"
                        as="h3"
                        className="text-slate-900 font-semibold mb-1"
                      />
                      <a href={`mailto:${contactEmail}`} className="text-[#3a00e0] text-sm transition-colors hover:underline underline-offset-2">
                        {contactEmail}
                      </a>
                      <EditableText
                        editKey="contact.info.email_note"
                        defaultValue="Réponse sous 24 h"
                        as="p"
                        className="text-slate-500 text-xs mt-0.5"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IconBadge icon={MapPin} variant="purple" size="sm" />
                    <div>
                      <EditableText
                        editKey="contact.info.centers_label"
                        defaultValue="Nos centres"
                        as="h3"
                        className="text-slate-900 font-semibold mb-1"
                      />
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {contactAddress}
                      </p>
                      <EditableText
                        editKey="contact.info.centers_note"
                        defaultValue={`${locations.length} centres sur la Côte d'Azur`}
                        as="p"
                        className="text-slate-500 text-xs mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
              </Reveal>

              <Reveal animation="fade-up" delay={120}>
              <div className="border-t border-slate-200 pt-8">
                <h3 className="text-slate-900 text-base font-semibold mb-4">
                  <EditableText
                    editKey="contact.faq.title"
                    defaultValue="Questions fréquentes"
                    as="span"
                    className="text-slate-900 text-base font-semibold"
                  />
                </h3>
                <ul className="space-y-3">
                  {[
                    { q: 'À partir de quel âge peut-on commencer ?', key: 'contact.faq.1.question' },
                    { q: 'Faut-il des connaissances préalables ?', key: 'contact.faq.2.question' },
                    { q: 'Le matériel est-il fourni ?', key: 'contact.faq.3.question' },
                    { q: "Combien d'enfants par groupe ?", key: 'contact.faq.4.question' },
                  ].map(({ q, key }) => (
                    <li key={key} className="text-slate-600 text-sm flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-[#3a00e0] shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden="true" />
                      <EditableText editKey={key} defaultValue={q} as="span" />
                    </li>
                  ))}
                </ul>
              </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos centres"
            eyebrowKey="contact.centers.eyebrow"
            title="Retrouvez-nous sur la Côte d'Azur"
            titleKey="contact.centers.title"
            subtitle={`Retrouvez-nous dans l'un de nos ${locations.length} centres`}
            subtitleKey="contact.centers.subtitle"
            reveal
          />

          {centersLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-600">
              <Loader2 className="h-8 w-8 text-[#3a00e0] animate-spin mb-3" />
              <p className="text-sm">Chargement des centres…</p>
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-12 text-slate-600">
              <MapPin className="h-8 w-8 text-[#3a00e0] mx-auto mb-3" aria-hidden="true" />
              <p className="text-sm">Aucun centre à afficher pour le moment.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {locations.map((location, i) => (
                <Reveal key={location.id} animation="fade-up" delay={i * 50}>
                <article className="bg-white border border-slate-200 rounded-xl overflow-hidden group h-full">
                  <div className="h-36 overflow-hidden">
                    <img
                      src={location.image}
                      alt={location.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 space-y-2.5 text-sm">
                    <h3 className="text-slate-900 text-lg font-semibold">{location.name}</h3>
                    <div className="flex items-start text-slate-600">
                      <MapPin className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0 mt-0.5" />
                      <span>{location.address}</span>
                    </div>
                    {location.phone && (
                      <div className="flex items-center text-slate-600">
                        <Phone className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0" />
                        <a href={`tel:${location.phone.replace(/\s/g, '')}`} className="hover:text-[#3a00e0] transition-colors">
                          {location.phone}
                        </a>
                      </div>
                    )}
                    {location.email && (
                      <div className="flex items-center text-slate-600">
                        <Mail className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0" />
                        <a href={`mailto:${location.email}`} className="truncate hover:text-[#3a00e0] transition-colors">
                          {location.email}
                        </a>
                      </div>
                    )}
                  </div>
                </article>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
